import React, { useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, ClipboardList, HeartPulse, Pill, TestTube2, IndianRupee, Database, Download, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const EXPORTS = [
  { label: "Members", desc: "All member profiles with categories, PINs, membership + verification status", icon: Users, path: "/export/members", tid: "export-members" },
  { label: "Enrollments", desc: "Pending, approved & rejected teacher enrollments with all applicant data", icon: ClipboardList, path: "/export/enrollments", tid: "export-enrollments" },
  { label: "Full Body Checkups", desc: "Every FBC booking with slot, amount, payment & booking status", icon: HeartPulse, path: "/export/orders/full_body_checkup", tid: "export-fbc" },
  { label: "Medicine Orders", desc: "Medicine orders with items, delivery status, prescriptions", icon: Pill, path: "/export/orders/medicine", tid: "export-medicine" },
  { label: "Lab Tests", desc: "Lab test bookings with tests requested, slot & report status", icon: TestTube2, path: "/export/orders/lab_test", tid: "export-lab" },
  { label: "Payments", desc: "All paid/refunded transactions across every service", icon: IndianRupee, path: "/export/payments", tid: "export-payments" },
  { label: "Complete Database Manifest", desc: "Single manifest listing counts + endpoints for every entity", icon: Database, path: "/export/complete", tid: "export-complete" },
];

export default function ExportPage() {
  const [busy, setBusy] = useState(null);

  const download = async (item) => {
    setBusy(item.path);
    try {
      const res = await api.get(item.path, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${item.label.toLowerCase().replace(/\s+/g, "_")}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${item.label} exported`);
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="p-8 space-y-6">
      <header>
        <div className="label-caps text-accent mb-2">Data export</div>
        <h1 className="font-heading text-4xl tracking-tight text-primary">Google Sheets export</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">
          Download any CRM dataset as a CSV file — open it in Google Sheets (File → Import → Upload) or Excel with one click.
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {EXPORTS.map((e) => {
          const Icon = e.icon;
          return (
            <Card key={e.path} className="p-6 border-border card-hover flex flex-col">
              <div className="flex items-start gap-3 mb-3">
                <div className="h-11 w-11 rounded-xl bg-secondary text-primary flex items-center justify-center shrink-0">
                  <Icon size={20}/>
                </div>
                <div>
                  <h3 className="font-heading text-lg text-foreground">{e.label}</h3>
                </div>
              </div>
              <p className="text-sm text-muted-foreground flex-1 mb-4">{e.desc}</p>
              <Button
                data-testid={e.tid}
                onClick={() => download(e)}
                disabled={busy === e.path}
                className="bg-primary hover:bg-primary/90 rounded-full self-start"
              >
                <Download size={14} className="mr-1.5"/> {busy === e.path ? "Preparing…" : "Download CSV"}
              </Button>
            </Card>
          );
        })}
      </div>

      <Card className="p-6 border-border bg-secondary/40">
        <div className="flex items-start gap-3">
          <FileSpreadsheet size={20} className="text-primary mt-0.5"/>
          <div>
            <div className="label-caps text-primary mb-1">How to open in Google Sheets</div>
            <ol className="text-sm text-muted-foreground list-decimal ml-5 space-y-1">
              <li>Click any <span className="font-medium">Download CSV</span> button above.</li>
              <li>Open <a className="underline" href="https://sheets.new" target="_blank" rel="noreferrer">sheets.new</a> to create a fresh Google Sheet.</li>
              <li>File → <span className="font-medium">Import</span> → Upload → drop the CSV → Import data.</li>
              <li>Done — the sheet is your live snapshot. Re-download any time to refresh.</li>
            </ol>
          </div>
        </div>
      </Card>
    </div>
  );
}
